import { MonitorSmartphone, Moon, Sun } from 'lucide-react';
import { useEffect } from 'react';
import { CopyButton } from './CopyButton';
import { DevModeBadge } from './DevModeBadge';
import { UserBadge } from './UserBadge';
import { TopbarTransportBadge } from './TopbarTransportBadge';
import { TopbarThroughputMeter } from './TopbarThroughputMeter';
import { useDevice } from '@/store/device';
import { useDeviceDisplayName } from '@/lib/displayName';
import { useGlobalPrefs, useGlobalPrefsUpdate } from '@/lib/myPrefs';

/**
 * Phase 2 Topbar — the `DevicePicker` was retired here because the URL
 * (`/devices/:serial/*`) already carries the active serial and the
 * devices grid is the canonical switcher. Leaving the picker would
 * duplicate state with the URL and break when the visible-device set
 * changes mid-session.
 */
export function Topbar() {
  // theme 是 B1 类「跟账号走」偏好。auto = 跟随系统；dark/light 显式覆盖。
  // 服务器初次拉数据前 store 给的是 'auto'，UI 先按系统 prefers-color-scheme
  // 渲染，等用户改主题再 PUT 回去。
  const theme = useGlobalPrefs((g) => g.ui.theme);
  const update = useGlobalPrefsUpdate();
  const dark =
    theme === 'dark'
      ? true
      : theme === 'light'
      ? false
      : typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add('dark');
    else root.classList.remove('dark');
    // Mirror to the index.html pre-React boot cache ONLY when the
    // store value is an explicit user preference. If it's still the
    // default 'auto' (B1 refresh hasn't landed yet — common on a
    // cold reload or when adh is offline), writing the matchMedia
    // -resolved dark/light here would silently OVERWRITE the cache
    // value that was set by the user's previous explicit toggle.
    // That's exactly the "切了深色，刷新又变浅色" bug:
    //   1. boot reads cache 'dark', paints dark   (good)
    //   2. React mounts with store default 'auto'
    //   3. system is light -> dark=false -> this useEffect would
    //      overwrite cache to 'light'
    //   4. user reloads -> boot reads 'light' -> permanently light
    // Solution: only persist when `theme` is dark/light. 'auto' is
    // perfectly handled by the boot script's prefers-color-scheme
    // fallback; we don't need to materialize it into the cache.
    if (theme !== 'dark' && theme !== 'light') return;
    try {
      localStorage.setItem('adh.theme.v1', theme);
    } catch {
      /* private mode / quota — accept the FOUC on next reload */
    }
  }, [dark, theme]);

  const toggleTheme = (): void => {
    update({ ui: { theme: dark ? 'light' : 'dark' } });
  };

  return (
    <header className="flex h-14 items-center justify-between border-b border-line bg-bg-card px-4">
      <div className="flex items-center gap-3">
        <FocusedDeviceChip />
      </div>
      <div className="flex items-center gap-2">
        <DevModeBadge />
        <TopbarTransportBadge />
        <TopbarThroughputMeter />
        <UserBadge />
        <button
          className="btn-ghost"
          aria-label="toggle theme"
          onClick={toggleTheme}
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          <span className="hidden sm:inline">{dark ? 'Light' : 'Dark'}</span>
        </button>
      </div>
    </header>
  );
}

/**
 * Compact "current device" chip shown on the left of the Topbar.
 *
 * Reads the focused serial from `useDevice` and the friendly label
 * from the shared `['devices']` cache (`useDeviceDisplayName` is a
 * free cache hit when the devices grid is already mounted).
 *
 * Hidden entirely when no device is focused (e.g. user is sitting
 * on the devices grid index page) so the bar doesn't show an empty
 * placeholder.
 */
function FocusedDeviceChip(): JSX.Element | null {
  const serial = useDevice((s) => s.serial);
  const isOnline = useDevice((s) => s.isOnline);
  const displayName = useDeviceDisplayName(serial);
  if (!serial) return null;
  const showAlias = displayName && displayName !== serial;
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-line bg-bg-soft/60 px-2 py-1 text-[12px]">
      <MonitorSmartphone
        className={`h-3.5 w-3.5 ${isOnline ? 'text-accent' : 'text-ink-dim'}`}
      />
      <span className="font-medium text-ink">
        {showAlias ? displayName : serial}
      </span>
      {showAlias && (
        <span className="font-mono text-[11px] text-ink-dim">({serial})</span>
      )}
      <CopyButton text={serial} label="复制 serial" size="sm" />
      {!isOnline && (
        <span className="ml-1 rounded bg-amber-500/15 px-1 text-[10px] font-medium text-amber-700 dark:text-amber-300">
          离线
        </span>
      )}
    </div>
  );
}
